import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ImageOff } from "lucide-react";

/**
 * Merchant-side thumbnail: fetches image_url from GET /photos/{id}.
 * List endpoints (/photos/mine, /photos/search) intentionally omit image_url
 * to keep responses under CDN limits — each thumb hydrates on-demand.
 *
 * - lazy=true (default): waits for IntersectionObserver (viewport root).
 * - lazy=false: fetches immediately on mount — use inside modals / scroll
 *   containers where the viewport-rooted observer can't see the scroll.
 */
const MerchantPhotoThumb = ({ photoId, alt = "", className = "", onClick, testid, lazy = true, fallbackSrc = null }) => {
  const ref = useRef(null);
  const [src, setSrc] = useState(fallbackSrc);
  const [state, setState] = useState(fallbackSrc ? "loaded" : "idle");

  useEffect(() => {
    if (fallbackSrc) return;
    let cancelled = false;
    const fetchNow = async () => {
      setState("loading");
      try {
        const r = await api.get(`/photos/${photoId}`);
        if (cancelled) return;
        setSrc(r.data.image_url);
        setState("loaded");
      } catch {
        if (!cancelled) setState("error");
      }
    };
    if (!lazy) {
      fetchNow();
      return () => { cancelled = true; };
    }
    const node = ref.current;
    if (!node) return () => { cancelled = true; };
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        fetchNow();
      },
      { rootMargin: "200px" }
    );
    io.observe(node);
    return () => { cancelled = true; io.disconnect(); };
  }, [photoId, lazy, fallbackSrc]);

  return (
    <div
      ref={ref}
      className={`w-full h-full bg-[#EBF4F6] flex items-center justify-center overflow-hidden ${className}`}
      onClick={() => src && onClick?.(src)}
      data-testid={testid}
    >
      {state === "loaded" && src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      ) : state === "error" ? (
        <div className="flex flex-col items-center gap-1 text-red-500">
          <ImageOff size={22} />
          <span className="text-[10px] font-bold">تعذّر التحميل</span>
        </div>
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#EBF4F6] to-[#7AB2B2]/40 animate-pulse" />
      )}
    </div>
  );
};

export default MerchantPhotoThumb;
